import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import Editor from '@monaco-editor/react';
import { Play, Sun, Moon, X } from 'lucide-react';
import Tooltip from './Tooltip';
import config from '../../src/config';

function PythonRunner({
  title = "Python Runner",
  initialCode = 'print("Hello, Python!")',
  className = "",
  editorHeight = "400px",
  maxOutputHeight = "300px",
  minOutputHeight = "60px",
}) {
  const [code, setCode] = useState(initialCode);
  const [output, setOutput] = useState('');
  const outputRef = useRef(null);
  const editorContainerRef = useRef(null);
  const { theme, toggleTheme } = useTheme();
  const [image, setImage] = useState(null);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  useEffect(() => {
    setOutput(''); // 每次 props 变化时更新状态
  }, ['']);
  useEffect(() => {
    setCode(initialCode); // 每次 props 变化时更新状态
  }, [initialCode]);
  useEffect(() => {
    function handleResize() {
      setShowLineNumbers(window.innerWidth >= 768);
    }
    handleResize(); // initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // 高级模态状态
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false); // 用于动画控制

  // 运行代码
  const runCode = async () => {
    try {
      const response = await axios.post(`${config.apiBaseUrl}/api/run-code/`, { code });
      setOutput(response.data.output);
      setImage(response.data.image);
    } catch (error) {
      setOutput(error.response?.data?.output || 'Error while running');
      setImage(null);
    }
  };

  // 输出区滚动到底
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // 阻止编辑器容器滚轮冒泡
  useEffect(() => {
    const editorContainer = editorContainerRef.current;
    if (editorContainer) {
      const handleWheel = (e) => {
        e.stopPropagation();
      };
      editorContainer.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        editorContainer.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  // 计算输出区域高度
  const calculateOutputHeight = () => {
    const maxHeightPx = parseInt(maxOutputHeight);
    const minHeightPx = parseInt(minOutputHeight);
    if (image) return `${maxHeightPx}px`;

    if (!output) return `${minHeightPx}px`;

    const lines = output.split('\n').length;
    const estimatedHeight = Math.max(lines * 20, minHeightPx);

    return `${Math.min(estimatedHeight, maxHeightPx)}px`;
  };

  // 打开模态层（先设显示，再触发动画）
  const openImageModal = () => {
    setIsImageModalOpen(true);
    setTimeout(() => setModalVisible(true), 10); // 延迟启动动画
  };

  // 关闭模态层（先动画消失，再隐藏）
  const closeImageModal = () => {
    setModalVisible(false);
    setTimeout(() => setIsImageModalOpen(false), 300); // 动画时长300ms后隐藏
  };

  // ESC键关闭模态层监听
  useEffect(() => {
    if (!isImageModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeImageModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isImageModalOpen]);

  const isDark = theme === 'vs-dark';
  const cardBg = isDark ? 'bg-[#1e1e1e]' : 'bg-white';
  const cardText = isDark ? 'text-white' : 'text-gray-900';
  const outputBg = isDark ? 'bg-[#0d1117]' : 'bg-gray-100';
  const outputText = isDark ? 'text-green-400' : 'text-gray-800';
  const outputBorder = isDark ? 'border-gray-600' : 'border-gray-300';

  return (
    <div className={`w-full ${className}`}>
      <div className={`${cardBg} ${cardText} border rounded-xl shadow overflow-hidden space-y-4 p-4 transition-colors duration-300`}>
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="flex space-x-2">
            <Tooltip text="Toggle Theme">
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-md bg-gray-600 text-white hover:bg-gray-500 transition"
                aria-label="Toggle theme"
              >
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </Tooltip>
            <Tooltip text="Ctrl+Enter or Cmd+Enter">
              <button
                onClick={runCode}
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm"
                aria-label="Run code"
              >
                <Play size={16} />
                <span className="hidden sm:inline">Run</span>
              </button>
            </Tooltip>
          </div>
        </div>

        <div 
          ref={editorContainerRef}
          className="relative"
          style={{ 
            height: editorHeight,
            overflow: 'hidden',
            border: `1px solid ${isDark ? '#404040' : '#e5e7eb'}`,
            borderRadius: '6px'
          }}
        >
          <Editor
            height="100%"
            defaultLanguage="python"
            value={code}
            onChange={(value) => setCode(value || '')}
            theme={theme}
            onMount={(editor, monaco) => {
              editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, runCode);
              editor.addCommand(
                monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
                () => {
                  editor.getAction('editor.action.formatDocument').run();
                }
              );
              const editorDomNode = editor.getDomNode();
              if (editorDomNode) {
                editorDomNode.style.overflow = 'hidden';
              }
            }}
            options={{
              fontSize: 14,
              fontFamily: 'monospace',
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'off',
              tabSize: 4,
              automaticLayout: true,
              lineNumbers: showLineNumbers ? 'on' : 'off',
              mouseWheelScrollSensitivity: 1,
              fastScrollSensitivity: 5,
              quickSuggestions: false,
              suggestOnTriggerCharacters: false,
              acceptSuggestionOnCommitCharacter: false,
              acceptSuggestionOnEnter: 'off',
              tabCompletion: 'off',
              wordBasedSuggestions: false,
              parameterHints: { enabled: false },
              hover: { enabled: false },
              formatOnType: true,
              formatOnPaste: true,
              autoIndent: 'full',
              semanticHighlighting: { enabled: true },
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
                useShadows: false,
                verticalHasArrows: false,
                horizontalHasArrows: false,
              },
            }}
          />
        </div>

        <div>
          <h3 className="text-base font-medium mb-2">Output</h3>
          <div
            className={`flex gap-4 ${outputBorder} border rounded-md transition-all duration-200 ease-in-out p-3`}
            style={{ 
              height: calculateOutputHeight(),
              overflow: 'hidden',
            }}
          >
            {/* Terminal Output */}
            <div
              ref={outputRef}
              className={`w-3/5 ${outputBg} ${outputText} rounded-md overflow-auto pr-2`}
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: isDark ? '#4a5568 #2d3748' : '#cbd5e0 #f7fafc',
              }}
            >
              <pre className="text-sm whitespace-pre font-mono leading-5 m-0 overflow-x-auto no-scrollbar">
                {output || (
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                    
                  </span>
                )}
              </pre>
            </div>

            {/* Image Output */}
            <div className="w-2/5 flex items-center justify-center bg-white dark:bg-[#0d1117] rounded-md cursor-pointer select-none" role="button" tabIndex={0}
              onClick={openImageModal}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openImageModal(); }}
              aria-label="View output image fullscreen"
              title="点击查看大图"
            >
              {image ? (
                <img
                  src={`data:image/png;base64,${image}`}
                  alt="Plot output"
                  className="max-h-full max-w-full object-contain"
                  draggable={false}
                />
              ) : (
                <span className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                  IMAGE
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 高级模态层 */}
      {isImageModalOpen && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 cursor-zoom-out
            transition-opacity duration-300
            ${modalVisible ? 'opacity-100' : 'opacity-0'}`}
          onClick={closeImageModal}
          role="dialog"
          aria-modal="true"
          aria-label="Fullscreen image preview"
        >
          <div
            className="relative max-w-[90vw] max-h-[90vh] cursor-default"
            onClick={(e) => e.stopPropagation()} // 点击图片不关闭
          >
            <button
              onClick={closeImageModal}
              className="absolute top-3 right-3 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Close fullscreen image"
            >
              <X size={24} />
            </button>
            <img
              src={`data:image/png;base64,${image}`}
              alt="Fullscreen plot"
              className="max-w-full max-h-full rounded-lg select-none"
              draggable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PythonRunner;

// Hide scrollbar but keep scrollability for .no-scrollbar
// You may move this CSS to your global stylesheet if preferred.
const style = document.createElement('style');
style.innerHTML = `
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
`;
if (typeof document !== 'undefined' && !document.getElementById('no-scrollbar-style')) {
  style.id = 'no-scrollbar-style';
  document.head.appendChild(style);
}